

import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styles from './scheduleCart.module.css';
import { Close } from '../../../Svg';
import { useDispatch, useSelector } from 'react-redux';
import { deleteProductfromScheduleThunk, getAllScheduleProductThunk, setscheduleData, upadteScheduleCart } from '../../redux/ScheduleSlice';
import { useNavigate } from 'react-router-dom';

const ScheduleCart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { scheduleData, refresh } = useSelector((state) => state.schedule);
  const [quantityErrors, setQuantityErrors] = useState({});
  const [totalQuantityErrors, setTotalQuantityErrors] = useState({});
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingTotalProductId, setEditingTotalProductId] = useState(null);

  useEffect(() => {
    dispatch(getAllScheduleProductThunk());
  }, [dispatch, refresh]);

  // Validate quantity against minimumMultiplier and total quantity
  const validateQuantity = (productId, value, totalQty) => {
    const product = scheduleData?.find(p => p.productId === productId);
    const minMultiplier = product?.productDetails?.inventory?.minimumMultiplier || 1;
    const numValue = Number(value);
    const numTotalQty = Number(totalQty);

    if (value === '') {
      setQuantityErrors(prev => ({ ...prev, [productId]: '' }));
      return false;
    }

    if (isNaN(numValue)) {
      setQuantityErrors(prev => ({ ...prev, [productId]: 'Please enter a valid number' }));
      return false;
    }

    if (numValue <= 0) {
      setQuantityErrors(prev => ({ ...prev, [productId]: 'Quantity must be greater than 0' }));
      return false;
    }

    if (numValue % minMultiplier !== 0) {
      setQuantityErrors(prev => ({
        ...prev,
        [productId]: `Quantity must be in multiples of ${minMultiplier}`
      }));
      return false;
    }

    if (numValue > numTotalQty) {
      setQuantityErrors(prev => ({
        ...prev,
        [productId]: `Cannot exceed total quantity (${totalQty})`
      }));
      return false;
    }

    setQuantityErrors(prev => ({ ...prev, [productId]: '' }));
    return true;
  };

  // Validate total quantity against minimumMultiplier
  const validateTotalQuantity = (productId, value) => {
    const product = scheduleData?.find(p => p.productId === productId);
    const minMultiplier = product?.productDetails?.inventory?.minimumMultiplier || 1;
    const numValue = Number(value);

    if (value === '') {
      setTotalQuantityErrors(prev => ({ ...prev, [productId]: '' }));
      return false;
    }

    if (isNaN(numValue)) {
      setTotalQuantityErrors(prev => ({ ...prev, [productId]: 'Please enter a valid number' }));
      return false;
    }

    if (numValue <= 0) {
      setTotalQuantityErrors(prev => ({ ...prev, [productId]: 'Quantity must be greater than 0' }));
      return false;
    }

    if (numValue % minMultiplier !== 0) {
      setTotalQuantityErrors(prev => ({
        ...prev,
        [productId]: `Quantity must be in multiples of ${minMultiplier}`
      }));
      return false;
    }

    setTotalQuantityErrors(prev => ({ ...prev, [productId]: '' }));
    return true;
  };

  const handleQuantityChange = (productId, newQuantity, totalQty) => {
    // First update the state immediately
    const updatedProducts = scheduleData?.map((product) => {
      if (product.productId === productId) {
        return {
          ...product,
          quantity: newQuantity === '' ? '' : parseInt(newQuantity, 10) || 0
        };
      }
      return product;
    });
    dispatch(setscheduleData(updatedProducts));

    // Then validate
    validateQuantity(productId, newQuantity, totalQty);

    // Only update server if validation passes and not currently editing
    if (editingProductId !== productId && validateQuantity(productId, newQuantity, totalQty)) {
      const data = {
        id: productId,
        quantity: newQuantity === '' ? '' : parseInt(newQuantity, 10) || 0,
        totalQty: totalQty
      };
      dispatch(upadteScheduleCart(data));
    }
  };

  const handleTotalQuantityChange = (productId, newQuantity, quantity) => {
    // First update the state immediately
    const updatedProducts = scheduleData?.map((product) => {
      if (product.productId === productId) {
        return {
          ...product,
          totalQty: newQuantity === '' ? '' : parseInt(newQuantity, 10) || 0
        };
      }
      return product;
    });
    dispatch(setscheduleData(updatedProducts));

    // Then validate
    validateTotalQuantity(productId, newQuantity);

    // Also validate if existing quantity is still valid with new total
    const product = scheduleData?.find(p => p.productId === productId);
    validateQuantity(productId, product?.quantity, newQuantity);

    // Only update server if validation passes and not currently editing
    if (editingTotalProductId !== productId && validateTotalQuantity(productId, newQuantity)) {
      const data = {
        id: productId,
        quantity: quantity,
        totalQty: newQuantity === '' ? '' : parseInt(newQuantity, 10) || 0
      };
      dispatch(upadteScheduleCart(data));
    }
  };

  const handleQuantityBlur = (productId, isTotal = false) => {
    if (isTotal) {
      setEditingTotalProductId(null);
    } else {
      setEditingProductId(null);
    }

    const product = scheduleData?.find(p => p.productId === productId);
    if (product) {
      if (isTotal) {
        const isValid = validateTotalQuantity(productId, product.totalQty);
        if (isValid) {
          dispatch(upadteScheduleCart({
            id: productId,
            quantity: product.quantity,
            totalQty: product.totalQty
          }));
        } else {
          // Reset to last valid total quantity
          const updatedProducts = scheduleData?.map((p) => {
            if (p.productId === productId) {
              return { ...p, totalQty: p.previousValidTotalQty || 1 };
            }
            return p;
          });
          dispatch(setscheduleData(updatedProducts));
        }
      } else {
        const isValid = validateQuantity(productId, product.quantity, product.totalQty);
        if (isValid) {
          dispatch(upadteScheduleCart({
            id: productId,
            quantity: product.quantity,
            totalQty: product.totalQty
          }));
        } else {
          // Reset to last valid quantity
          const updatedProducts = scheduleData?.map((p) => {
            if (p.productId === productId) {
              return { ...p, quantity: p.previousValidQuantity || 1 };
            }
            return p;
          });
          dispatch(setscheduleData(updatedProducts));
        }
      }
    }
  };

  const handleQuantityFocus = (productId, isTotal = false) => {
    // Store the current valid quantity before editing
    const updatedProducts = scheduleData?.map((product) => {
      if (product.productId === productId) {
        return {
          ...product,
          previousValidQuantity: isTotal ? product.previousValidQuantity : product.quantity,
          previousValidTotalQty: isTotal ? product.totalQty : product.previousValidTotalQty
        };
      }
      return product;
    });
    dispatch(setscheduleData(updatedProducts));

    if (isTotal) {
      setEditingTotalProductId(productId);
    } else {
      setEditingProductId(productId);
    }
  };

  const getBreakQtyDetails = (quantityTable, quantity) => {
    if (!quantityTable || !Array.isArray(quantityTable) || quantityTable.length === 0) {
      return { breakQty: 0, unitPrice: 0, finalPrice: 0 };
    }
    const sortedTable = [...quantityTable].sort((a, b) => (a?.breakQty || 0) - (b?.breakQty || 0));
    return sortedTable.find((entry) => quantity <= entry?.breakQty) || sortedTable[sortedTable.length - 1] || { breakQty: 0, unitPrice: 0, finalPrice: 0 };
  };

  const calculateEstimatedTax = () => {
    return scheduleData?.reduce((totalTax, item) => {
      const qty = Number(item.totalQty);
      const priceRow = item.productDetails.quantityTable?.[0];

      if (!priceRow) return totalTax;

      const unitPrice = Number(priceRow.unitPrice);
      const finalPrice = Number(priceRow.finalPrice);

      if (isNaN(unitPrice) || isNaN(finalPrice) || isNaN(qty)) {
        return totalTax;
      }

      const taxPerUnit = finalPrice - unitPrice;

      return totalTax + taxPerUnit * qty;
    }, 0);
  };

  const formatAmount = (val) => {
    const num = Number(val) || 0;
    if (num < 0) {
      return `-₹${Math.abs(num).toFixed(2)}`;
    }
    return `₹${num.toFixed(2)}`;
  };

  const calculateTotal = () => {
    return scheduleData?.reduce((total, product) => {
      const { finalPrice = 0 } = getBreakQtyDetails(
        product.productDetails?.quantityTable,
        product.totalQty
      ) || {};
      return total + product.totalQty * finalPrice;
    }, 0);
  };

  const handleDelete = (id) => {
    dispatch(deleteProductfromScheduleThunk(id));
  };

  const isQuantityValid = (productId) => {
    const product = scheduleData?.find(p => p.productId === productId);
    if (!product) return false;

    const quantity = product.quantity;
    const totalQty = product.totalQty;
    const minMultiplier = product.productDetails?.inventory?.minimumMultiplier || 1;

    return (
      quantity > 0 &&
      totalQty > 0 &&
      quantity % minMultiplier === 0 &&
      totalQty % minMultiplier === 0 &&
      quantity <= totalQty
    );
  };

  const validateBeforeProceeding = () => {
    const hasInvalidQuantity = scheduleData?.some(item => !isQuantityValid(item.productId));
    if (hasInvalidQuantity) {
      toast.error('Please enter valid quantities (multiples of SPQ) for all items');
      return false;
    }

    return true;
  };

  const handleProceed = () => {
    let isValid = validateBeforeProceeding();

    console.log("isvalid----", isValid)
    if (isValid) {
      navigate('/schedulePO');
    }
  };

  if (scheduleData?.length <= 0) {
    return (
      <div style={{ flex: 1, justifyContent: 'center', alignItems: 'center', display: 'flex', minHeight: '60vh' }}>
        <div className={styles.emptyContainer}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#F4F0FF', color: '#512DA8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, marginBottom: 8 }}>📅</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1E1B2E' }}>No Product found</div>
          <button style={{ marginTop: 8, padding: '12px 28px', background: '#512DA8', color: '#FFFFFF', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/categories')}>Browse Products</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.imgBread}>
        <div className={styles.breadContent}>
          <span>Schedule Tool Cart</span>
          <p>{scheduleData?.length || 0} {scheduleData?.length === 1 ? 'item' : 'items'} scheduled for PO upload</p>
        </div>
      </div>
      <div className={styles.scheduleContainer}>
        <div className={styles.bomContentBox}>
          <h3>Cart : {scheduleData?.length || 0} items</h3>

          <div className={styles.previewContainer}>
            <table className={styles.previewtable}>
              <thead>
                <tr>
                  <th className={styles.previewth}>Sl No</th>
                  <th className={styles.previewth}> Part #</th>
                  <th className={styles.previewth}>Manufacture</th>
                  <th className={styles.previewth}>Total Quantity</th>
                  <th className={styles.previewth}>Schedule Quantity</th>
                  <th className={styles.previewth}>Unit price</th>
                  <th className={styles.previewth}>Subtotal </th>
                </tr>
              </thead>
              <tbody>
                {scheduleData?.map((item, index) => {
                  const { unitPrice = 0 } = getBreakQtyDetails(
                    item.productDetails?.quantityTable,
                    item.totalQty
                  ) || {};
                  const minMultiplier = item?.productDetails?.inventory?.minimumMultiplier || 1;

                  return (
                    <React.Fragment key={item.id}>
                      <tr className={styles.previewstripedRow}>
                        <td className={styles.previewtd}>{index + 1}</td>
                        <td className={styles.previewtd}>
                          <div
                            className={styles.prodprevlink}
                            onClick={() => navigate(`/productCode/${item?.productDetails?.productOverview?.productNumber}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            {item?.productDetails?.productOverview?.productNumber}
                          </div>
                        </td>
                        <td className={styles.previewtd}>{item?.productDetails?.productOverview?.manufacturerName}</td>
                        <td className={styles.previewtd}>
                          <div className={styles.qtyCell}>
                            <input
                              type="number"
                              placeholder="Enter"
                              id={`total-quantity-${item?.productId}`}
                              onChange={(e) =>
                                handleTotalQuantityChange(item?.productId, e.target.value, item?.quantity)
                              }
                              onBlur={() => handleQuantityBlur(item?.productId, true)}
                              onFocus={() => handleQuantityFocus(item?.productId, true)}
                              value={item?.totalQty}
                              className={styles.addCatInput}
                              min="1"
                              step={minMultiplier}
                            />
                            {totalQuantityErrors[item?.productId] && (
                              <div className={styles.errorText}>
                                {totalQuantityErrors[item?.productId]}
                              </div>
                            )}
                            <div className={styles.spqText}>
                              SPQ: {minMultiplier}
                            </div>
                          </div>
                        </td>
                        <td className={styles.previewtd}>
                          <div className={styles.qtyCell}>
                            <input
                              type="number"
                              placeholder="Enter"
                              className={styles.addCatInput}
                              id={`quantity-${item?.productId}`}
                              onChange={(e) =>
                                handleQuantityChange(item?.productId, e.target.value, item?.totalQty)
                              }
                              onBlur={() => handleQuantityBlur(item?.productId)}
                              onFocus={() => handleQuantityFocus(item?.productId)}
                              value={item?.quantity}
                              min="1"
                              max={item?.totalQty}
                              step={minMultiplier}
                            />
                            {quantityErrors[item?.productId] && (
                              <div className={styles.errorText}>
                                {quantityErrors[item?.productId]}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className={styles.previewtd}>₹{unitPrice}</td>
                        <td className={styles.previewtd}>
                          <div className={styles.closetd}>
                            <span className={styles.subtotalVal}>₹{(item?.totalQty * unitPrice).toFixed(2)}</span>
                            <div
                              className={styles.removeBtn}
                              onClick={() => handleDelete(item?.productId)}
                              title="Remove Item"
                            >
                              <Close />
                            </div>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={styles.displayFlexBtwn}>
            <h2>{calculateEstimatedTax() < 0 ? 'Discount / Tax Adjustment' : 'Estimated Tax'}</h2>
            <h2 style={calculateEstimatedTax() < 0 ? { color: '#10B981', fontWeight: 700 } : {}}>
              {formatAmount(calculateEstimatedTax())}
            </h2>
          </div>
          <div className={styles.displayFlexBtwn}>
            <h2>Estimated Shipping & Handling</h2>
            <h2>₹0</h2>
          </div>
          <div className={styles.displayFlexBtwn}>
            <h2>Total</h2>
            <h2>₹{(calculateTotal())?.toFixed(2)}</h2>
          </div>

          <div className={styles.displayFlex}>
            <button
              className={styles.submitBtn}
              onClick={handleProceed}
              disabled={scheduleData?.some(item => !isQuantityValid(item.productId))}
            >
              Upload PO & Continue
            </button>
            <button
              className={styles.cancelBtn}
              onClick={() => navigate('/categories')}
            >
              Add More items
            </button>
          </div>
        </div>
        <div className={styles.bomTabsCol2}>
          <p>Note:</p>
          <ol>
            <li>You can send the PO to this email <a href='mailto:company@electrowiz.com' className={styles.download}>company@electrowiz.com</a> directly.</li>
            <li>You can view your open order in your <a href='/myprofile' className={styles.download}>User dashboard</a></li>
          </ol>
        </div>
      </div>
    </>
  );
};

export default ScheduleCart;



